import React, { FC, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import CtaButton from 'components/top-service-mode/actions/cta-button';
import { useDistanceText } from 'hooks/geolocation/use-distance-text';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { appendObjectToQueryString } from 'hooks/navigation/utils';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { useReversedUIContext } from 'state/reversed-ui';
import { useStoreContext } from 'state/store';
import { theme } from 'styles/configure-theme';
import { routes } from 'utils/routing';

import ChooseLocation from './actions/choose-location';
import {
  ArrowRightIcon,
  ButtonWrapper,
  ClickableWrapper,
  Container,
  Details,
  DetailsContainer,
  Heading,
  IconContainer,
  IconStyled,
} from './styled';
import { ITopServiceModeProps } from './types';

// Maximum allowed number of characters for the selected location address as per DS
const maxChars = 24;

/**
 * Abstraction point for top service UI/feature roll out of future ordering
 */
export const TopServiceModeView: FC<React.PropsWithChildren<ITopServiceModeProps>> = ({
  heading,
  details,
  icon,
  rbiOrderId,
  openModal,
}) => {
  const { formatMessage } = useIntl();
  const { reversedUI } = useReversedUIContext();
  const { logEvent } = useMParticleContext();
  const { isAuthenticated } = useAuthContext();
  const { noStoreSelected, store: restaurant } = useStoreContext();
  const { serviceMode, curbsidePickupOrderId, isPreConfirmCurbside } = useOrderContext();
  const { linkTo, navigate } = useNavigation();
  const iconColor = reversedUI ? theme.token('icon-reversed') : theme.token('icon-default');
  const isDesktop = useIsDesktopBreakpoint();
  const distanceText = useDistanceText({ restaurant, shouldUseUserCoordinatesIfAvailable: true });

  const customDetails = useMemo(
    () =>
      typeof details !== 'string' || details.length <= maxChars || isDesktop
        ? details
        : `${details.substring(0, maxChars)}...`,
    [details, isDesktop]
  );

  const handleServiceModeChange = useCallback(() => {
    logEvent(CustomEventNames.TOP_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
    });

    if (isPreConfirmCurbside() && isAuthenticated) {
      return openModal && openModal();
    }

    if (serviceMode === ServiceMode.DELIVERY) {
      return navigate(routes.address);
    }

    navigate(routes.serviceMode);
  }, [isAuthenticated, isPreConfirmCurbside, logEvent, navigate, serviceMode]);

  const handleNavigateToOrderConfirmation = (rbiOrderId: string, showToast?: boolean) => {
    logEvent(CustomEventNames.TOP_SERVICE_MODE, EventTypes.Other, {
      'Service Mode': serviceMode,
      'Order Id': rbiOrderId,
    });
    linkTo(appendObjectToQueryString(`${routes.orderConfirmation}/${rbiOrderId}`, { showToast }));
  };

  const getCtaButton = () => {
    if (isPreConfirmCurbside()) {
      return (
        <CtaButton
          text={formatMessage({ id: 'confirmArrival' })}
          cta={() => handleNavigateToOrderConfirmation(curbsidePickupOrderId, true)}
        />
      );
    } else if (rbiOrderId) {
      return (
        <CtaButton
          text={formatMessage({ id: 'trackOrder' })}
          cta={() => handleNavigateToOrderConfirmation(rbiOrderId)}
        />
      );
    }

    return (
      <CtaButton
        text={serviceMode === ServiceMode.DELIVERY ? formatMessage({ id: 'change' }) : distanceText}
        cta={handleServiceModeChange}
        icon={<ArrowRightIcon />}
      />
    );
  };

  const button = getCtaButton();

  return (
    <Container testID="top-service-mode">
      <ClickableWrapper
        dd-action-name="top-service-mode-wrapper"
        testID="top-service-mode-wrapper"
        onPress={handleServiceModeChange}
        accessibilityRole="link"
        height="$12"
      >
        <IconContainer>
          <IconStyled color={iconColor} variant={icon} />
        </IconContainer>
        <DetailsContainer justifyContent={noStoreSelected ? 'space-between' : null}>
          <Heading reversedUI={reversedUI}>{heading}</Heading>
          {details ? (
            <Details reversedUI={reversedUI}>{customDetails}</Details>
          ) : (
            <ChooseLocation testID="top-service-choose-location" reversedUI={reversedUI} />
          )}
        </DetailsContainer>
      </ClickableWrapper>
      {button && !noStoreSelected && <ButtonWrapper>{button}</ButtonWrapper>}
    </Container>
  );
};
